import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminGetMeetingRequestById,
  adminGetMeetingRequests,
  adminRespondMeetingRequest,
  adminUpdateMeetingRequest,
  adminUpdateMeetingRequestTutorPaymentStatus,
} from './adminApi';
import { IUser } from '../../UserSlice';
import { getMeetingRequestById, getMeetingRequests } from './api';
import { ICourse } from '../CoursesSlice';
import {
  ISection,
  TLessonTypes,
  TMeetingSectionPaymentType,
} from '../SectionSlice';
import { TUserSelectOption } from '../../../components/Inputs/admin/UserListDropdown';
import {
  TMeetingRequestRequestedByRoleOption,
  TMeetingRequestTypeOption,
} from '../../../constants';
import { IManualAdjustment } from '../ManualAdjustmentsSlice';

export const MEETING_REQUEST_STATUSES = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;

export const MEETING_REQUEST_TYPES = {
  CANCEL: 'CANCEL',
  RESCHEDULE: 'RESCHEDULE',
  DELETE: 'DELETE',
} as const;

export const MEETING_REQUEST_REQUESTED_BY_ROLES = {
  STUDENT: 'STUDENT',
  TUTOR: 'TUTOR',
  ADMIN: 'ADMIN',
} as const;

export const MEETING_REQUEST_TUTOR_PAYMENT_STATUSES = {
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
} as const;

export type TMeetingRequestStatus = typeof MEETING_REQUEST_STATUSES[keyof typeof MEETING_REQUEST_STATUSES];
export type TMeetingRequestType = typeof MEETING_REQUEST_TYPES[keyof typeof MEETING_REQUEST_TYPES];
export type TMeetingRequestRequestedByRole = typeof MEETING_REQUEST_REQUESTED_BY_ROLES[keyof typeof MEETING_REQUEST_REQUESTED_BY_ROLES];
export type TMeetingRequestTutorPaymentStatus = typeof MEETING_REQUEST_TUTOR_PAYMENT_STATUSES[keyof typeof MEETING_REQUEST_TUTOR_PAYMENT_STATUSES];

export interface IMeetingRequest {
  _id: string;
  eventId?: string;
  course: string | ICourse;
  section: string | ISection;
  sectionDetails: ISection;
  subject: string;
  curriculum: string;
  grade: string;
  start: string;
  end: string;
  duration: number;
  studentIds: Array<string | IUser>;
  tutorIds: Array<string | IUser>;
  students: IUser[];
  tutors: IUser[];
  paymentTutor: string | IUser;
  lessonType: TLessonTypes;
  paymentType: TMeetingSectionPaymentType;
  penalty?: number;
  penaltyAmount?: number;
  penaltyReason?: string;
  penaltyMessage?: string;
  status: TMeetingRequestStatus;
  type: TMeetingRequestType;
  requestedBy: Partial<IUser>;
  requestedByRole: TMeetingRequestRequestedByRole;
  tutorPaymentStatus: TMeetingRequestTutorPaymentStatus;
  reason: string;
  tutor?: string | IUser;
  student?: string | IUser;
  newStart?: string;
  newEnd?: string;
  adminNote?: string;
  createdAt: string;
  tutorRate?: number;
  tutorRateCurrency?: string;
  tutorAmount?: number;
  tutorAmountCurrency?: string;
  allocatedTutorAmount?: number;
  isPenaltyAppealRequestReceived?: boolean;
  respondedBy?: string | IUser;
  respondedAt: string;
  referenceId: string;
}

export interface IAdminMeetingRequestCommonFilterData {
  startDate?: string;
  endDate?: string;
  selectedStudent?: TUserSelectOption;
  selectedTutor?: TUserSelectOption;
  selectedRequestedByRole?: TMeetingRequestRequestedByRoleOption;
  selectedRequestType?: TMeetingRequestTypeOption;
}

export interface TMeetingRequestTutorAmountData {
  manualAdjustments: Array<IManualAdjustment>;
  tutorPenaltyAmountData: { [key: string]: number };
  studentPenaltyAmountData: { [key: string]: number };
  // Paid
  paidTotalData: { [key: string]: number };
  pendingTotalData: { [key: string]: number };
  paidTutorPenaltyData: { [key: string]: number };
  paidStudentPenaltyData: { [key: string]: number };
  paidPenaltyWaiverData: { [key: string]: number };
  manualAdjustmentData: { [key: string]: number };
  paidAmountData: { [key: string]: number };
  lessonHistoryPaidTutorPenalties: { [key: string]: number };
  // PENDING
  pendingTutorPenaltyData: { [key: string]: number };
  pendingStudentPenaltyData: { [key: string]: number };
  pendingPenaltyWaiverData: { [key: string]: number };
  pendingAmountData: { [key: string]: number };
  lessonHistoryPendingTutorPenalties: { [key: string]: number };
}

export interface IInitialMeetingRequestsState {
  status: 'idle' | 'loading' | 'rejected';
  meetingRequestListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IMeetingRequest>;
    tutorAmountData?: TMeetingRequestTutorAmountData;
  };
  meetingRequestData?: IMeetingRequest;
  // ADMIN
  adminMeetingRequestListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IMeetingRequest>;
    tutorAmountData?: TMeetingRequestTutorAmountData;
  };
  adminMeetingRequestData?: IMeetingRequest;
  adminMeetingRequestCommonFilterData?: IAdminMeetingRequestCommonFilterData;
}

const initialState: IInitialMeetingRequestsState = {
  status: 'idle',
  meetingRequestListData: undefined,
  meetingRequestData: undefined,
  // ADMIN
  adminMeetingRequestListData: undefined,
  adminMeetingRequestData: undefined,
  adminMeetingRequestCommonFilterData: undefined,
};

export const MeetingRequestsSlice = createSlice({
  name: 'meetingRequests',
  initialState,
  reducers: {
    resetMeetingRequestListData: (state) => {
      state.meetingRequestListData = undefined;
    },
    updateMeetingRequestListItem: (
      state,
      action: PayloadAction<{
        _id: string;
        attributes: Partial<IMeetingRequest>;
      }>
    ) => {
      const result = action.payload;

      if (state.meetingRequestListData) {
        const newMeetingRequestListData = { ...state.meetingRequestListData };
        const newList = newMeetingRequestListData.list || [];

        const index = newList.findIndex((item) => item._id === result._id);

        if (index > -1) {
          newList[index] = { ...newList[index], ...(result.attributes || {}) };
        }

        newMeetingRequestListData.list = [...newList];

        state.meetingRequestListData = newMeetingRequestListData;
      }
    },
    resetMeetingRequestData: (state) => {
      state.meetingRequestData = undefined;
    },
    // ADMIN
    resetAdminMeetingRequestListData: (state) => {
      state.adminMeetingRequestListData = undefined;
    },
    resetAdminMeetingRequestData: (state) => {
      state.adminMeetingRequestData = undefined;
    },
    updateAdminMeetingRequestCommonFilterData: (
      state,
      action: PayloadAction<Partial<IAdminMeetingRequestCommonFilterData>>
    ) => {
      const newAttributes = action.payload || {};

      if (state.adminMeetingRequestCommonFilterData) {
        state.adminMeetingRequestCommonFilterData = {
          ...state.adminMeetingRequestCommonFilterData,
          ...newAttributes,
        };
      } else {
        state.adminMeetingRequestCommonFilterData = { ...newAttributes };
      }
    },
    resetAdminMeetingRequestCommonFilterData: (state) => {
      state.adminMeetingRequestCommonFilterData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMeetingRequests.fulfilled, (state, action) => {
        const result = action.payload;

        const newZoomRequestListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.meetingRequestListData = newZoomRequestListData;
      })
      .addCase(getMeetingRequestById.fulfilled, (state, action) => {
        const result = action.payload;

        state.meetingRequestData = result;
      });

    // ADMIN APIS
    builder
      .addCase(adminGetMeetingRequests.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminMeetingRequestListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.adminMeetingRequestListData = newAdminMeetingRequestListData;
      })
      .addCase(adminGetMeetingRequestById.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminMeetingRequestData = result;
      })
      .addCase(adminRespondMeetingRequest.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminMeetingRequestData = result;

        if (
          state.adminMeetingRequestListData &&
          state.adminMeetingRequestListData.list &&
          state.adminMeetingRequestListData.list.length
        ) {
          const newAdminMeetingRequestListData = {
            ...state.adminMeetingRequestListData,
          };

          newAdminMeetingRequestListData.list = newAdminMeetingRequestListData.list.filter(
            (item) => item._id !== result._id
          );

          if (
            newAdminMeetingRequestListData.totalDocs &&
            newAdminMeetingRequestListData.totalDocs > 0
          ) {
            newAdminMeetingRequestListData.totalDocs -= 1;
          }

          state.adminMeetingRequestListData = newAdminMeetingRequestListData;
        }
      })
      .addCase(adminUpdateMeetingRequest.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminMeetingRequestData = result;
      })
      .addCase(
        adminUpdateMeetingRequestTutorPaymentStatus.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminMeetingRequestListData &&
            result.tutorPaymentStatus &&
            result.meetingRequestIds &&
            result.meetingRequestIds.length
          ) {
            const newAdminMeetingRequestListData = {
              ...state.adminMeetingRequestListData,
            };

            const newList = [...state.adminMeetingRequestListData.list].map(
              (item) => {
                if ((result.meetingRequestIds || []).includes(item._id)) {
                  return {
                    ...item,
                    tutorPaymentStatus: result.tutorPaymentStatus,
                  };
                }

                return item;
              }
            );

            newAdminMeetingRequestListData.list = newList;

            state.adminMeetingRequestListData = newAdminMeetingRequestListData;
          }
        }
      );

    // Common Matchers
    builder.addMatcher(
      isPending(
        getMeetingRequests,
        getMeetingRequestById,
        // Admin
        adminGetMeetingRequests,
        adminGetMeetingRequestById,
        adminRespondMeetingRequest,
        adminUpdateMeetingRequest,
        adminUpdateMeetingRequestTutorPaymentStatus
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getMeetingRequests,
        getMeetingRequestById,
        // Admin
        adminGetMeetingRequests,
        adminGetMeetingRequestById,
        adminRespondMeetingRequest,
        adminUpdateMeetingRequest,
        adminUpdateMeetingRequestTutorPaymentStatus
      ),
      (state) => {
        state.status = 'rejected';
      }
    );

    builder.addMatcher(
      isFulfilled(
        getMeetingRequests,
        getMeetingRequestById,
        // Admin
        adminGetMeetingRequests,
        adminGetMeetingRequestById,
        adminRespondMeetingRequest,
        adminUpdateMeetingRequest,
        adminUpdateMeetingRequestTutorPaymentStatus
      ),
      (state) => {
        state.status = 'idle';
      }
    );
  },
});

export const getMeetingRequestsState = (
  state: RootState
): RootState['meetingRequests'] => state.meetingRequests;

export const {
  updateMeetingRequestListItem,
  resetMeetingRequestData,
  // Admin
  resetAdminMeetingRequestListData,
  resetAdminMeetingRequestData,
  resetMeetingRequestListData,
  updateAdminMeetingRequestCommonFilterData,
  resetAdminMeetingRequestCommonFilterData,
} = MeetingRequestsSlice.actions;

export * from './adminApi';
export default MeetingRequestsSlice.reducer;
